import { ChevronLeftIcon, ChevronRightIcon, DotsHorizontalIcon } from '@radix-ui/react-icons';
import * as React from 'react';

import { ButtonProps, buttonVariants } from '@/components/ui/button';
import { cn } from '@/utils/shadcn';
import { FormattedMessage } from 'react-intl';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn('g-mx-auto g-flex g-w-full g-justify-center', className)}
    {...props}
  />
);
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<'ul'>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      className={cn('g-flex g-flex-row g-items-center g-gap-1', className)}
      {...props}
    />
  )
);
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<'li'>>(
  ({ className, ...props }, ref) => <li ref={ref} className={cn('', className)} {...props} />
);
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'a'>;

const PaginationLink = ({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      buttonVariants({
        variant: isActive ? 'outline' : 'ghost',
        size,
      }),
      className
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  isRtl,
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { isRtl?: boolean }) => (
  <PaginationLink
    aria-label="Go to previous page"
    size="default"
    className={cn('g-gap-1 g-ps-2.5', className)}
    {...props}
  >
    {isRtl ? (
      <ChevronRightIcon className="g-h-4 g-w-4" />
    ) : (
      <ChevronLeftIcon className="g-h-4 g-w-4" />
    )}
    <span className="g-hidden sm:g-inline">
      <FormattedMessage id="pagination.previous" />
    </span>
  </PaginationLink>
);
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
  isRtl,
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { isRtl?: boolean }) => (
  <PaginationLink
    aria-label="Go to next page"
    size="default"
    className={cn('g-gap-1 g-pe-2.5', className)}
    {...props}
  >
    <span className="g-hidden sm:g-inline">
      <FormattedMessage id="pagination.next" />
    </span>
    {isRtl ? (
      <ChevronLeftIcon className="g-h-4 g-w-4" />
    ) : (
      <ChevronRightIcon className="g-h-4 g-w-4" />
    )}
  </PaginationLink>
);
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('g-flex g-h-9 g-w-9 g-items-center g-justify-center', className)}
    {...props}
  >
    <DotsHorizontalIcon className="g-h-4 g-w-4" />
    <span className="g-sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
